import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';

import authUtil from 'util/auth';
import Page404 from 'pages/auth/Page404';
import Page401 from 'pages/auth/Page401';
import Page500 from 'pages/auth/Page500';
import allRouteList from './allRouteList';
import Page401Partner from '../../pages/auth/Page401Partner';


const PageRouteList = (props) => {
   const [, setLastRenderedAt] = useState(new Date());

   /**
    * Triggers component re-render. This is required in order to keep the page access rights
    * synchronized with the logged in user's access rights.
    */
   useEffect(() => {
      setLastRenderedAt(new Date());
   }, [
      props.userRoleListPartner,
      props.userEmployerPrivileges,
      props.userRoleListEmployer,
   ]);

   const mapRouteItemList = ({ exact = true,
      ...routeItem }) => {
      return props.isLoading
         ? null
         : (
            <Route
               key={ routeItem.path }
               path={ routeItem.path }
               exact={ exact }
               render={
                  () => {
                     // DEPRECATING (AOE-backed) pages are gone — AOE was scaled to
                     // zero on 2026-08-13, so every call from them 503s. Everyone
                     // gets the Partner login, DE token or not. The AOE-free ported
                     // twins have no such gate. Deliberately NOT the DE sign-in:
                     // that page is unlisted and nothing may reveal its URL, not
                     // even this wall.
                     if (routeItem.requiresDeSession) {
                        return <Redirect to='/auth/login/jobcase'/>;
                     }
                     const hasAccessToPath = authUtil.hasAuthAccessBase({
                        access: routeItem?.access,
                        userRoleListEmployer: props.userRoleListEmployer,
                        userRoleListPartner: props.userRoleListPartner,
                        userEmployerPrivileges: props.userEmployerPrivileges,
                     });

                     return hasAccessToPath
                        ? <routeItem.component/>
                        : <Page401/>;
                  }
               }
            />
         );
   };

   return (
      <Switch>
         {
            allRouteList.map(mapRouteItemList)
         }
         <Route exact path='/employer/applicant/list'>
            <Redirect to='/employer/candidate/'/>
         </Route>
         <Route
            exact
            path='/employer/applicant/:id/profile'
            render={ (renderProps) => (<Redirect to={ `/employer/candidate/${ renderProps.match.params.id }` }/>) }
         />
         <Route
            exact
            path='/employer/applicant/:id/grade/up'
            render={ (renderProps) => (
               <Redirect to={ {
                  pathname: `/employer/candidate/${ renderProps.match?.params?.id }`,
                  search: '?rate=up',
               } }
               />
            ) }
         />
         <Route
            exact
            path='/employer/applicant/:id/grade/down'
            render={ (renderProps) => (
               <Redirect to={ {
                  pathname: `/employer/candidate/${ renderProps.match?.params?.id }`,
                  search: '?rate=down',
               } }
               />
            ) }
         />
         <Route
            exact
            path='/employer/applicant/:id/grade/side'
            render={ (renderProps) => (
               <Redirect to={ {
                  pathname: `/employer/candidate/${ renderProps.match.params.id }`,
                  search: '?rate=maybe',
               } }
               />
            ) }
         />
         <Route exact path='/employer/cc/list'>
            <Redirect to='/employer/billing'/>
         </Route>
         <Route exact path='/employer/job/list'>
            <Redirect to='/employer/job'/>
         </Route>
         <Route exact path='/employer/auth/login'>
            <Redirect to='/auth/login/jobcase'/>
         </Route>
         {
            /*
             * DE shutdown: the DE login is gone entirely — AOE was scaled to zero
             * on 2026-08-13 and the break-glass form was deleted with it. Bookmarked
             * /auth/login/employer links (plus DE logout and session-expiry pushes
             * from withRedirectToLogin) and the old unlisted break-glass URL all
             * forward to the Partner login. location.state is preserved so
             * resolvePostLoginRedirect still sees the intended destination.
             */
         }
         <Route
            exact
            path={ ['/auth/login/employer', '/auth/login/employer-deprecated-removed'] }
            render={ (renderProps) => (
               <Redirect to={ {
                  pathname: '/auth/login/jobcase',
                  state: renderProps.location?.state,
               } }
               />
            ) }
         />
         {
            /*
             * LUT-508: Syndicated Sites is retired from the UI (nav entry removed, routes
             * unregistered). Bookmarked /syn-sites/* links land here and are sent through the
             * partner login route, whose mount effect forwards an already-logged-in partner to
             * their role-appropriate default landing page (getDefaultPartnerLandingPath). The
             * LUT-499 redirect-loop guard makes this safe. Restore the section by reverting the
             * LUT-508 commit.
             */
         }
         <Route path='/syn-sites'>
            <Redirect to='/auth/login/jobcase'/>
         </Route>
         <Route exact path='/auth/401'>
            <Page401/>
         </Route>
         <Route exact path='/auth/500'>
            <Page500/>
         </Route>
         <Route exact path='/auth/404'>
            <Page404/>
         </Route>
         <Route exact path='/401/pt'>
            <Page401Partner/>
         </Route>

         <Route>
            <Page404/>
         </Route>
      </Switch>
   );
};

const mapStateToProps = (state) => {
   return ({
      userRoleListEmployer: state?.authReducer?.loggedInUser?.roles || [],
      userRoleListPartner: state?.authReducer?.loggedInUserPartner?.roles || [],
      userEmployerPrivileges: state?.privilegeReducer?.privileges || null,
      isLoading: state.authReducer.global.isLoading,
   });
};

PageRouteList.propTypes = {
   userRoleListEmployer: PropTypes.arrayOf(PropTypes.string),
   userRoleListPartner: PropTypes.arrayOf(PropTypes.string),
   userEmployerPrivileges: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.instanceOf(null)]),
};

PageRouteList.defaultProps = {
   userRoleListEmployer: [],
   userRoleListPartner: [],
   userEmployerPrivileges: null,
};



export default connect(mapStateToProps)(PageRouteList);
